/**
 * Netz-Teil zu `unterrisiken.ts`: legt PV-Anlage/Öltank eines gespeicherten
 * Gebäudes als Kind-Risiken an bzw. aktualisiert vorhandene Kinder.
 *
 * Läuft NACH dem Speichern des Gebäudes. Schlägt er fehl, ist das Gebäude
 * trotzdem gespeichert — der Aufrufer meldet das getrennt.
 */
import { apiUrl } from "../../utils/api";
import {
  baueUnterRisiken,
  fuehreMerkmaleZusammen,
  gleicheMitKindernAb,
  type VorhandenesKind,
} from "./unterrisiken";

export type GebaeudeZiel = {
  /** Id des gespeicherten Gebäude-Risikos (= parentRiskObjectId). */
  riskObjectId: number;
  /** Antworten des Gebäudes. */
  answers: Record<string, unknown>;
  gebaeudeLabel?: string;
  /** Vorhandene Kinder (nur beim Bearbeiten, aus GET risk-objects/:id). */
  kinder?: VorhandenesKind[];
};

/**
 * @returns Anzahl angelegter + aktualisierter Kinder. Wirft bei HTTP-Fehlern.
 */
export const speichereUnterRisiken = async (args: {
  adresse: Record<string, unknown>;
  gebaeude: GebaeudeZiel[];
  contactID: number | null | undefined;
  contractID: number | null | undefined;
}): Promise<number> => {
  const neu: Array<Record<string, unknown>> = [];
  const updates: Array<{ id: number; body: Record<string, unknown>; art: Parameters<typeof fuehreMerkmaleZusammen>[1] }> = [];

  for (const g of args.gebaeude) {
    const entwuerfe = baueUnterRisiken(args.adresse, g.answers, g.gebaeudeLabel);
    for (const { entwurf, existingId } of gleicheMitKindernAb(entwuerfe, g.kinder ?? [])) {
      if (existingId === null) {
        neu.push({
          contractID: args.contractID ?? null,
          contactID: args.contactID ?? null,
          riskType: entwurf.riskType,
          name: entwurf.name,
          parentRiskObjectId: g.riskObjectId,
          characteristics: entwurf.characteristics,
        });
      } else {
        updates.push({
          id: existingId,
          art: entwurf,
          body: {
            ...(args.contactID ? { contactID: args.contactID } : {}),
            name: entwurf.name,
            parentRiskObjectId: g.riskObjectId,
          },
        });
      }
    }
  }

  if (neu.length > 0) {
    const res = await fetch(apiUrl("risk-objects/bulk"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: neu }),
    });
    if (!res.ok) throw new Error(await res.text());
  }

  for (const u of updates) {
    // Vorhandene Merkmale holen: der PUT ersetzt alle, von Hand am Kind
    // ergänzte sollen bleiben.
    const alt = await fetch(apiUrl(`risk-objects/${u.id}`));
    if (!alt.ok) throw new Error(await alt.text());
    const kind = (await alt.json()) as {
      characteristics?: Array<{ nameBezeichnung?: string; riskCharacteristicValue?: string }>;
    };
    const res = await fetch(apiUrl(`risk-objects/${u.id}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...u.body,
        characteristics: fuehreMerkmaleZusammen(kind.characteristics ?? [], u.art),
      }),
    });
    if (!res.ok) throw new Error(await res.text());
  }

  return neu.length + updates.length;
};
